import React from 'react';
import { Tooltip } from 'antd';
import { EEligibilityStatus } from 'types/insuranceEligibility';
import s from './s.module.less';

// 状态 → 展示配置。Clients 列表列和 Financial 发票明细 Result 列都从这里读，
// 两个 surface 对同一个患者不能给出不同说法。
export const ELIGIBILITY_STATUS_CONFIG: Record<EEligibilityStatus, { label: string; className: string; tip: string }> =
    {
        [EEligibilityStatus.ELIGIBLE]: {
            label: 'Eligible',
            className: s.eligible,
            tip: 'Coverage was active when we last checked and the payer returned benefit detail. Eligibility is not a guarantee of payment.',
        },
        [EEligibilityStatus.INACTIVE]: {
            label: 'Inactive',
            className: s.inactive,
            tip: 'The payer reported no active coverage on the date of service. This visit cannot be billed to this plan as it stands.',
        },
        // 2026-09-17 起没有任何地方派生这个状态（只显示后端给的），配置留着以防后端将来下发
        [EEligibilityStatus.NEEDS_RECHECK]: {
            label: 'Needs re-check',
            className: s.recheck,
            tip: 'The last check is older than the 7-day freshness window, so coverage may have changed since. Re-check before the visit.',
        },
        [EEligibilityStatus.COULD_NOT_VERIFY]: {
            label: 'Could not verify',
            className: s.unverified,
            tip: 'The payer rejected the request. This is usually a data-entry mismatch (member ID, date of birth, name) rather than missing coverage.',
        },
        [EEligibilityStatus.NO_COST_ESTIMATE]: {
            label: 'No cost estimate',
            className: s.limited,
            tip: 'Coverage is active and this visit can be billed. The payer returned no copay or coinsurance for it, so there is no figure to quote the patient. Unreported does not mean $0.',
        },
        [EEligibilityStatus.OUT_OF_NETWORK]: {
            label: 'Out-of-network',
            className: s.oon,
            // 10/05：OON 现在有两种成因（payer 说的 / plan type 不在 service template）。列表接口不带原因，
            // 所以 tip 两种都说，列表与详情同一句
            tip: 'Coverage is active, but this provider is out of network for it: either the payer says so, or the plan type is not on the provider’s service template.',
        },
        [EEligibilityStatus.CHECKED]: {
            label: 'Checked',
            className: s.checked,
            tip: 'The payer answered, but the coverage status could not be determined from the response.',
        },
        [EEligibilityStatus.NEVER_CHECKED]: {
            label: 'Never checked',
            className: s.never,
            tip: 'No eligibility check has ever run for this patient. Insurance is on file, so a check can be run.',
        },
        [EEligibilityStatus.NO_INSURANCE]: {
            label: 'No insurance',
            className: s.noins,
            tip: 'No insurance on file for this patient, so there is nothing to verify.',
        },
        [EEligibilityStatus.PAYER_NOT_MATCHED]: {
            label: 'Payer not matched',
            className: s.unverified,
            tip: 'The stored carrier could not be matched to a clearinghouse payer, so no request was sent and nothing was billed.',
        },
        [EEligibilityStatus.CHECK_FAILED]: {
            label: 'Check failed',
            className: s.unverified,
            // 不提收费：这张表是静态的、看不见 persona，而 managed 从不付费——计费句归结果卡（原型注释）
            tip: 'The attempt never reached the payer (timeout or gateway error), so there is no answer to show yet.',
        },
        [EEligibilityStatus.SERVICE_NOT_COVERED]: {
            label: 'Service not covered',
            className: s.oon,
            tip: 'The plan is active, but the payer states this service is excluded. A claim for this visit would be denied.',
        },
        [EEligibilityStatus.CONFLICTING_AMOUNTS]: {
            label: 'Conflicting amounts',
            className: s.limited,
            // 原型句尾还有 "with the payer's own label on each"——每行原文标签等后端 conflictBenefits
            // 加字段后一起点亮，届时补回
            tip: 'The payer sent more than one amount for this visit and nothing says which applies, so no price is shown. The rows they sent are listed on the card.',
        },
    };

type Props = {
    status: EEligibilityStatus;
    // 列表列带 ? 图标 + hover 解释；发票明细等紧凑场景可关掉
    showTip?: boolean;
    // 同一状态在不同成因下 tip 不同（如 carve-out 触发的 No cost estimate）时覆盖默认文案
    tipOverride?: string;
};

const EligibilityStatusTag: React.FC<Props> = ({ status, showTip = true, tipOverride }) => {
    const config = ELIGIBILITY_STATUS_CONFIG[status];
    if (!config) {
        return null;
    }
    const tag = (
        <span className={`${s.tag} ${config.className}`}>
            {config.label}
            {showTip && (
                <svg className={s.tipIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" strokeWidth="1.5" />
                    <path
                        d="M7.7 7.7a2.35 2.35 0 1 1 2.85 2.3c-.55.13-.55.62-.55 1.1"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                    />
                    <circle cx="10" cy="14.1" r="1" fill="currentColor" stroke="none" />
                </svg>
            )}
        </span>
    );
    if (!showTip) {
        return tag;
    }
    return (
        <Tooltip title={tipOverride || config.tip} overlayStyle={{ maxWidth: 320 }}>
            {tag}
        </Tooltip>
    );
};

export default EligibilityStatusTag;
